import type React from 'react';

import type { CardDef } from '@unmatched/engine';
import { fighterLabel, localizeCard } from '@unmatched/content';

import { useCardDisplay, type CardVariant } from './cardDisplay.js';
import { cardImageUrl } from '../cardImage.js';

/**
 * Akčná karta. Jedna šablóna, všetko ostatné sú dáta — meno, typ, hodnota,
 * boost, banner (kto ju smie zahrať) aj texty efektov rozpadnuté na fázy.
 * Vďaka tomu je karta čitateľná pri akejkoľvek veľkosti a kvalite obrázka
 * a raz sa dá preložiť bez zásahu do vykresľovania (viď `content/i18n`).
 *
 * Ako sa karta kreslí, hovorí `useCardDisplay` — obrázok, obrázok s ostrým
 * textom, alebo iba text. Karta bez obrázka je vždy šablóna.
 */

/** Typ karty tak, ako je vytlačený na karte — teda po anglicky. */
const TYPE_LABEL: Record<CardDef['type'], string> = {
  attack: 'ATTACK',
  defense: 'DEFENSE',
  versatile: 'VERSATILE',
  scheme: 'SCHEME',
};

/** Návestia fáz podľa rulebooku. `basic` je text bez fázy. */
const PHASE_LABEL = {
  immediate: 'IMMEDIATELY',
  during: 'DURING COMBAT',
  after: 'AFTER COMBAT',
} as const;

interface Props {
  def: CardDef;
  disabled?: boolean;
  selected?: boolean;
  faceDown?: boolean;
  /** Počas manévru je dôležitá boost hodnota, nie útok/obrana. */
  highlightBoost?: boolean;
  /**
   * O koľko je karta práve teraz silnejšia, než má vytlačené (Sinbadove VOYAGE).
   * Bez toho vyzerá efekt ako pokazený — na karte stojí 2, zasiahne za 5.
   */
  valueBonus?: number;
  /** Väčšie prevedenie do dialógu s balíčkom. */
  large?: boolean;
  /** Zdvihnutá kópia karty nad rukou — čitateľná veľkosť. */
  huge?: boolean;
  onClick?: () => void;
  /**
   * Hlási, ktorá karta je pod kurzorom, aj kde presne na obrazovke leží —
   * zdvihnutá kópia sa podľa toho ukotví nad svoje miesto v ruke.
   */
  onHover?: (def: CardDef | null, rect?: DOMRect) => void;
}

export function Card({
  def,
  disabled,
  selected,
  faceDown,
  highlightBoost,
  valueBonus = 0,
  large,
  huge,
  onClick,
  onHover,
}: Props) {
  const { variant, locale } = useCardDisplay();

  if (faceDown) {
    return <div className="card card--back" aria-label="Karta lícom dole" />;
  }

  // Bez obrázka nie je z čoho robiť art ani hybrid. V režime `tooltip` zostáva
  // karta nedotknutá — všetko vysvetlenie je v bublinke vedľa nej.
  const shown: CardVariant = !def.image ? 'template' : variant === 'tooltip' ? 'art' : variant;
  const text = localizeCard(def, locale);
  const banner = fighterLabel(def.character) ?? 'ANY';

  const className = [
    'card',
    `card--${def.type}`,
    `card--${shown}`,
    large ? 'card--large' : '',
    huge ? 'card--huge' : '',
    selected ? 'is-selected' : '',
    highlightBoost ? 'is-boost' : '',
    disabled ? 'is-dimmed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Karta, ktorá sa práve nedá zahrať, NIE JE `disabled` — na disabled tlačidlo
  // sa v prehliadači nedá prejsť myšou a hráč by si ju nemohol ani prečítať.
  const guard = disabled ? undefined : onClick;
  const hover = onHover
    ? {
        onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) =>
          onHover(def, e.currentTarget.getBoundingClientRect()),
        onMouseLeave: () => onHover(null),
      }
    : {};

  const effects = (
    <span className="card__effects">
      {text.effects.basic ? <span className="card__effect">{text.effects.basic}</span> : null}
      {(['immediate', 'during', 'after'] as const).map((phase) =>
        text.effects[phase] ? (
          <span key={phase} className={`card__effect card__effect--${phase}`}>
            <b>{PHASE_LABEL[phase]}:</b> {text.effects[phase]}
          </span>
        ) : null,
      )}
    </span>
  );

  return (
    <button
      type="button"
      className={className}
      aria-disabled={disabled}
      onClick={guard}
      // Žiadny `title`: natívna bublina prehliadača by sa po sekunde vysunula
      // cez vlastný náhľad karty a prekryla presne to, čo si hráč chce prečítať.
      aria-label={`${text.name} · ${TYPE_LABEL[def.type]} ${def.value} · boost ${def.boost}`}
      {...hover}
    >
      {shown === 'template' ? null : (
        // Bez `draggable={false}` by obrázok spustil natívne ťahanie prehliadača
        // a to našemu prehadzovaniu kariet pošle `pointercancel`.
        <img
          className="card__art"
          src={cardImageUrl(def.id)}
          alt=""
          loading="lazy"
          draggable={false}
        />
      )}

      {shown === 'template' ? (
        <>
          <span className="card__band">{TYPE_LABEL[def.type]}</span>
          <span className="card__value">{def.value}</span>
          <span className="card__banner">{banner}</span>
          <span className="card__name">{text.name}</span>
          {effects}
          <span className="card__boost">
            boost <b>{def.boost}</b>
          </span>
        </>
      ) : null}

      {shown === 'hybrid' ? (
        // Doštička sadá presne na miesto, kde má text aj tlačená karta — nič
        // podstatné z artu neprekryje, len ho nahradí ostrým textom.
        <span className="card__plate">
          <span className="card__banner">{banner}</span>
          <span className="card__name">{text.name}</span>
          {effects}
        </span>
      ) : null}

      {/* Na arte je boost drobný — počas manévru ho zvýrazníme. */}
      {shown !== 'template' && highlightBoost ? (
        <span className="card__boostBadge">+{def.boost}</span>
      ) : null}

      {/* Zosilnenie z efektu: vytlačené číslo klame, tak ho doplníme. */}
      {valueBonus > 0 && !highlightBoost ? (
        <span className="card__valueBadge" title="silnejšia vďaka efektu">
          {def.value + valueBonus}
        </span>
      ) : null}
    </button>
  );
}
